<template>
  <div class="apropos-page">
    <!-- Hero -->
    <section class="hero container-xl">
      <div class="hero-text">
        <span class="eyebrow"><span class="pulse-dot"></span>Apprenti développeur</span>
        <h1 class="hero-title">
          Bonjour, je suis <span class="text-gradient">Alexis Dupain</span>
        </h1>
        <p class="hero-subtitle">
          Je conçois des applications web modernes, de l'interface à la base de données.
          Découvrez mon parcours, mes projets et les technologies que j'utilise.
        </p>
        <div class="hero-actions">
          <router-link to="/projets" class="btn-gradient">
            <i class="bi bi-grid"></i> Voir mes projets
          </router-link>
          <router-link to="/contact" class="btn-outline">
            <i class="bi bi-envelope"></i> Me contacter
          </router-link>
        </div>
        <div class="hero-social">
          <a href="https://github.com/adupain" target="_blank" rel="noopener" class="social-btn" aria-label="GitHub"><i class="bi bi-github"></i></a>
          <a href="https://www.instagram.com/al.dup40" target="_blank" rel="noopener" class="social-btn" aria-label="Instagram"><i class="bi bi-instagram"></i></a>
          <a href="mailto:dupainalexis@gmail.com" class="social-btn" aria-label="Email"><i class="bi bi-envelope"></i></a>
        </div>
      </div>

      <div class="hero-visual">
        <div class="avatar-wrapper">
          <img
            src="../../../public/profile.jpg"
            alt="Alexis Dupain"
            :class="['profile-image', { 'spin-3d': avatarSpinning }]"
            @error="imageError = true"
            v-if="!imageError"
            @click="triggerAvatarSpin"
            role="button"
            tabindex="0"
            @keyup.enter="onAvatarKey"
            @animationend="onAvatarAnimationEnd"
          >
          <div class="profile-placeholder" v-else @click="triggerAvatarSpin" role="button" tabindex="0" @keyup.enter="onAvatarKey">
            <i class="bi bi-person-circle" :class="{ 'spin-3d': avatarSpinning }" @animationend="onAvatarAnimationEnd"></i>
          </div>
          <div class="floating-chip chip-top"><i class="bi bi-code-slash"></i> Vue.js</div>
          <div class="floating-chip chip-bottom"><i class="bi bi-database"></i> Supabase</div>
        </div>
      </div>
    </section>

    <!-- Technologies -->
    <section class="container-xl tech-section">
      <TechStack />
    </section>

    <div class="container-xl">
      <!-- Section À Propos -->
      <section id="apropos" class="section">
        <div class="section-head">
          <span class="section-kicker">01 — À propos</span>
          <h2 class="section-title">Qui suis-je ?</h2>
        </div>
        <div class="surface-card about-card">
          <div class="description-loading text-center" v-if="loading">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>

          <div v-else-if="error" class="alert alert-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Erreur de connexion : {{ error }}
          </div>

          <p v-else-if="description" class="about-text">{{ description }}</p>

          <div v-else class="alert alert-info">
            <i class="bi bi-info-circle me-2"></i>
            Aucune description disponible. Ajoutez-en une dans Supabase (table APropos).
          </div>
        </div>
      </section>

      <!-- Section Formations - Frise Chronologique -->
      <section id="formations" class="section">
        <div class="section-head">
          <span class="section-kicker">02 — Parcours</span>
          <h2 class="section-title">Mon parcours de formation</h2>
        </div>
          <div class="formations-section">

            <div v-if="loadingFormations" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Chargement...</span>
              </div>
            </div>

            <div v-else-if="formations.length === 0" class="text-center text-muted">
              <i class="bi bi-inbox display-4 mb-3"></i>
              <p>Aucune formation disponible pour le moment.</p>
            </div>

            <!-- Frise Chronologique avec carrousel -->
            <div v-else class="timeline-wrapper">
              <button class="timeline-nav prev" @click="scrollPrev" :aria-disabled="!canScrollLeft" aria-label="Précédent"><i class="bi bi-chevron-left"></i></button>
              <div ref="timelineContainer" class="timeline">
                <div 
                  v-for="formation in formations" 
                  :key="formation.id"
                  class="timeline-item"
                >
                  <div class="timeline-marker">
                    <div class="timeline-dot" :class="getStatusColorClass(formation.status)"></div>
                  </div>
                  <div class="timeline-content">
                    <!-- Make card clickable when getCardLink returns a URL (formation.link or keyword match) -->
                    <template v-if="getCardLink(formation)">
                      <a class="timeline-link" :href="getCardLink(formation)" target="_blank" rel="noopener noreferrer" :aria-label="'Ouvrir ' + (formation.titre || 'la formation')">
                        <div class="timeline-card">
                        <div class="timeline-card-top">
                          <span class="timeline-year">{{ formation.annee }}</span>
                          <span class="badge" :class="getStatusBadgeClass(formation.status)">{{ formation.status }}</span>
                        </div>
                        <h4 class="timeline-title">{{ formation.titre }}</h4>
                        <p class="timeline-description">{{ formation.description }}</p>
                        </div>
                      </a>
                    </template>
                    <template v-else>
                      <div class="timeline-card">
                        <div class="timeline-card-top">
                          <span class="timeline-year">{{ formation.annee }}</span>
                          <span class="badge" :class="getStatusBadgeClass(formation.status)">{{ formation.status }}</span>
                        </div>
                        <h4 class="timeline-title">{{ formation.titre }}</h4>
                        <p class="timeline-description">{{ formation.description }}</p>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <button class="timeline-nav next" @click="scrollNext" :aria-disabled="!canScrollRight" aria-label="Suivant"><i class="bi bi-chevron-right"></i></button>
              <div class="timeline-dots" v-if="pagesCount > 1">
                <button
                  v-for="n in pagesCount"
                  :key="n"
                  :class="{ active: (n - 1) === activePage }"
                  @click="goToPage(n - 1)"
                  :aria-label="'Aller à la page ' + n"
                ></button>
              </div>
            </div>
          </div>
      </section>
    </div>
  </div>
</template>

<script>
import { portfolioAPI } from '../../lib/portfolioAPI'
import TechStack from './TechStack.vue'

export default {
  name: 'HomePage',
  components: { TechStack },
  data() {
    return {
      description: '',
      loading: true,
      error: null,
      formations: [],
      loadingFormations: true,
      imageError: false,
      avatarSpinning: false,
      // carousel state
      itemWidth: 0,
      canScrollLeft: false,
      canScrollRight: false,
      itemsPerPage: 3,
      pagesCount: 1,
      activePage: 0
    }
  },
  async mounted() {
    console.log('🏠 HomePage mounted - Début chargement')
    
    // Timeout de sécurité pour éviter le chargement infini
    const timeoutId = setTimeout(() => {
      if (this.loading || this.loadingFormations) {
        console.warn('⚠️ Timeout - Arrêt forcé du chargement')
        this.loading = false
        this.loadingFormations = false
      }
    }, 10000) // 10 secondes max
    
    // Charger les données en parallèle pour plus de rapidité
    try {
      await Promise.all([
        this.loadAPropos(),
        this.loadFormations()
      ])
      console.log('✅ HomePage - Chargement terminé')
    } catch (error) {
      console.error('❌ HomePage - Erreur lors du chargement:', error)
      this.loading = false
      this.loadingFormations = false
    } finally {
      clearTimeout(timeoutId)
    }
    
    // attach window resize to recompute carousel sizes
    window.addEventListener('resize', this.updateCarousel)
  },
  watch: {
    formations() {
      this.$nextTick(() => this.updateCarousel())
    }
  },
  beforeUnmount() {
      try {
        window.removeEventListener('resize', this.updateCarousel)
        const el = this.$refs.timelineContainer
        if (el && el.removeEventListener) el.removeEventListener('scroll', this.onScroll)
      } catch (e) {
        // noop
      }
    },

  methods: {
    triggerAvatarSpin() {
      this.avatarSpinning = true
    },
    onAvatarKey() {
      this.triggerAvatarSpin()
    },
    onAvatarAnimationEnd() {
      this.avatarSpinning = false
    },

    async loadAPropos() {
      console.log('📖 Chargement À propos...')
      try {
        const { data, error, fromCache } = await portfolioAPI.getAPropos()

        if (fromCache) {
          console.log('✅ Données "À propos" chargées depuis le cache')
        } else {
          console.log('📥 Données "À propos" chargées depuis la BDD')
        }

        if (error) {
          this.error = error.message
          console.error('❌ Erreur Supabase:', error)
          return
        }
        
        if (data && data.description) {
          this.description = data.description
          console.log('✅ Description chargée:', this.description.substring(0, 50) + '...')
        } else {
          console.log('⚠️ Aucune description trouvée')
        }
      } catch (err) {
        this.error = err.message
        console.error('❌ Erreur chargement À propos:', err)
      } finally {
        this.loading = false
        console.log('🏁 loadAPropos terminé, loading =', this.loading)
      }
    },
    
    async loadFormations() {
      console.log('🎓 Chargement formations...')
      try {
        const { data, error, fromCache } = await portfolioAPI.getFormations()

        if (fromCache) {
          console.log('✅ Formations chargées depuis le cache')
        } else {
          console.log('📥 Formations chargées depuis la BDD')
        }

        if (error) {
          console.error('❌ Erreur chargement formations:', error)
          return
        }
        
        this.formations = data || []
        console.log('✅ Formations chargées:', this.formations.length, 'formations')
      } catch (err) {
        console.error('❌ Erreur chargement formations:', err)
      } finally {
        this.loadingFormations = false
        console.log('🏁 loadFormations terminé, loadingFormations =', this.loadingFormations)
      }
    },
    
    getStatusColorClass(status) {
      const classes = {
        'en-cours': 'dot-warning',
        'En cours': 'dot-warning',
        'terminé': 'dot-success',
        'Terminé': 'dot-success',
        'prévu': 'dot-info',
        'Prévu': 'dot-info'
      }
      return classes[status] || 'dot-warning'
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        'en-cours': 'badge-warning',
        'En cours': 'badge-warning',
        'terminé': 'badge-success',
        'Terminé': 'badge-success',
        'prévu': 'badge-info',
        'Prévu': 'badge-info'
      }
      return classes[status] || 'badge-warning'
    },

    getCardLink(formation) {
      if (!formation) return null
      if (formation.link) return formation.link
      const title = (formation.titre || '').toString().toLowerCase()
      if (title.includes('concepteur')) return 'https://fabriquenumerique.fr/'
      if (title.includes('supérieur')) return 'https://louisdefoix.fr/formations/bts-tertiaires/bts-sio/'
      if (title.includes('baccalauréat')) return 'https://www.mfr-dax.com/'
      if (title.includes('collèges')) return 'https://salins.mon-ent-occitanie.fr/'
      return null
    },

    // Carousel methods
    updateCarousel() {
      const el = this.$refs.timelineContainer
      if (!el) return
      // each page shows two items, so the page width is the visible clientWidth
      this.itemWidth = el.clientWidth
      // determine number of pages based on scrollWidth / clientWidth
      this.pagesCount = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth))
      // attach scroll listener once
      if (!this._timelineScrollAttached) {
        el.addEventListener('scroll', this.onScroll, { passive: true })
        this._timelineScrollAttached = true
      }
      this.updateScrollState()
    },

    updateScrollState() {
      const el = this.$refs.timelineContainer
      if (!el) return
      this.canScrollLeft = el.scrollLeft > 10
      this.canScrollRight = el.scrollLeft + el.clientWidth + 10 < el.scrollWidth
      // update active page using a ratio to map scroll position to page index
      if (this.pagesCount > 1) {
        const maxScroll = Math.max(1, el.scrollWidth - el.clientWidth)
        const ratio = (el.scrollLeft || 0) / maxScroll
        this.activePage = Math.round(ratio * (this.pagesCount - 1))
      } else {
        this.activePage = 0
      }
    },

    scrollNext() {
      // move to next page (use goToPage to ensure dots update correctly)
      const next = Math.min(this.pagesCount - 1, this.activePage + 1)
      this.goToPage(next)
    },

    scrollPrev() {
      // move to previous page
      const prev = Math.max(0, this.activePage - 1)
      this.goToPage(prev)
    }
    ,
    onScroll() {
      // throttle-ish update
      if (this._scrollTimeout) clearTimeout(this._scrollTimeout)
      this._scrollTimeout = setTimeout(() => {
        this.updateScrollState()
      }, 80)
    },
    goToPage(pageIndex) {
      const el = this.$refs.timelineContainer
      if (!el) return
      const left = (el.clientWidth || 0) * pageIndex
      el.scrollTo({ left, behavior: 'smooth' })
      setTimeout(() => this.updateScrollState(), 350)
    }
  }
}
</script>

<style scoped>
@import '../timeline-styles.css';

/* ---------- Hero ---------- */
.hero {
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  align-items: center;
  gap: 3rem;
  min-height: calc(100vh - 88px);
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.hero-title {
  font-family: var(--font-display);
  font-size: clamp(2.4rem, 5.5vw, 4.25rem);
  line-height: 1.05;
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 1.25rem 0 1.25rem;
  color: var(--text-primary);
}

.hero-subtitle {
  font-size: 1.15rem;
  color: var(--text-secondary);
  max-width: 34rem;
  margin-bottom: 2rem;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

.hero-social {
  display: flex;
  gap: 0.6rem;
}

.hero-visual {
  display: flex;
  justify-content: center;
}

.avatar-wrapper {
  position: relative;
  padding: 10px;
  border-radius: 50%;
  background: var(--gradient-primary);
  box-shadow: var(--shadow-glow);
}

.profile-image,
.profile-placeholder {
  display: block;
  width: clamp(220px, 28vw, 340px);
  height: clamp(220px, 28vw, 340px);
  border-radius: 50%;
  object-fit: cover;
  border: 6px solid var(--bg-primary);
  cursor: pointer;
  transition: var(--transition);
}

.profile-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
}

.profile-placeholder i {
  font-size: 8rem;
  color: var(--primary);
}

.profile-image:hover,
.profile-placeholder:hover {
  transform: scale(1.02);
}

.spin-3d {
  animation: spin3d 0.9s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes spin3d {
  from { transform: perspective(800px) rotateY(0); }
  to { transform: perspective(800px) rotateY(360deg); }
}

.floating-chip {
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  background: var(--glass);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-md);
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-primary);
  animation: float 5s ease-in-out infinite;
}

.floating-chip i { color: var(--primary); }
.chip-top { top: 8%; left: -12%; }
.chip-bottom { bottom: 10%; right: -10%; animation-delay: -2.5s; }

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.tech-section {
  margin-bottom: 2rem;
}

/* ---------- À propos ---------- */
.about-card {
  padding: clamp(1.5rem, 4vw, 3rem);
}

.about-text {
  font-size: 1.125rem;
  line-height: 1.85;
  color: var(--text-secondary);
  white-space: pre-line;
  margin: 0;
}

/* ---------- Carrousel formations ---------- */
.timeline-wrapper {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
}

.timeline-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--border-light);
  background: var(--surface);
  color: var(--text-primary);
  box-shadow: var(--shadow-md);
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: var(--transition);
}

.timeline-nav:hover:not([aria-disabled="true"]) {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

.timeline-nav.prev { left: -6px; }
.timeline-nav.next { right: -6px; }
.timeline-nav[aria-disabled="true"] { opacity: 0.35; cursor: default; }

.timeline-dots { display: flex; gap: 8px; justify-content: center; margin-top: 0.5rem; }
.timeline-dots button {
  width: 8px;
  height: 8px;
  padding: 0;
  border-radius: 999px;
  border: none;
  background: var(--border-medium);
  cursor: pointer;
  transition: var(--transition);
}
.timeline-dots button.active { width: 28px; background: var(--gradient-primary); }

.timeline-link {
  display: block;
  color: inherit;
  text-decoration: none;
  border-radius: var(--radius-lg);
}
.timeline-link:focus-visible {
  outline: 3px solid var(--primary-light);
  outline-offset: 4px;
}
.timeline-link .timeline-card { cursor: pointer; }

/* ---------- Responsive ---------- */
@media (max-width: 900px) {
  .hero {
    grid-template-columns: 1fr;
    text-align: center;
    min-height: auto;
    padding-top: 1rem;
    gap: 2.5rem;
  }
  .hero-visual { order: -1; }
  .hero-subtitle { margin-left: auto; margin-right: auto; }
  .hero-actions, .hero-social { justify-content: center; }
  .chip-top { left: -4%; }
  .chip-bottom { right: -4%; }
}
</style>
